// Project status within a month
export type ProjectStatus = 'planned' | 'active' | 'almost' | 'done';

// Project category (maps to colors)
export type ProjectCategory = 'tech' | 'health' | 'personal' | 'finance' | 'travel' | 'other';

// Scheduling state: backlog (unscheduled) or scheduled (has dates)
export type ProjectSchedulingState = 'backlog' | 'scheduled';

export interface Project {
  id: string;
  title: string;
  category: ProjectCategory;
  status: ProjectStatus;
  schedulingState: ProjectSchedulingState;
  monthKey: string | null;
  originMonthKey: string | null;
  isTarget: boolean;
  isPriority: boolean;
  prefix?: string;
  notes?: string;
  tags?: TagRef[];
  // Date range support
  startDate?: string | null;
  endDate?: string | null;
  targetDate?: string | null;
  /** @deprecated weekKey is now computed client-side from startDate via getWeekKey() */
  weekKey?: string | null;
  // Task summary
  taskCounts?: {
    total: number;
    done: number;
    todo: number;
  };
  // Incomplete tasks preview (up to 10, sorted by priority desc then created_at desc)
  incompleteTasks?: { id: string; title: string; priority: TaskPriority }[];
  // Span info (set when project appears in a week context)
  totalWeeks?: number;
  weekIndex?: number;
  multiWeek?: boolean;
  archivedAt?: string | null;
  isArchived?: boolean;
}

// Task types
export type TaskStatus = 'todo' | 'done' | 'wont_do';
export type TaskPriority = 'none' | 'low' | 'medium' | 'high';
export type TaskSortBy = 'position' | 'priority' | 'created_at' | 'title';
export type TaskSortDirection = 'asc' | 'desc';
export type TaskGroupBy = 'none' | 'priority';

export interface TaskViewPreferences {
  sort_by: TaskSortBy;
  sort_direction: TaskSortDirection;
  group_by: TaskGroupBy;
  show_completed?: boolean;
}

/** Which task fields "Copy incomplete as…" includes (the title is always copied). */
export interface TaskCopyPreferences {
  description: boolean;
  tags: boolean;
  priority: boolean;
  created_at: boolean;
  updated_at: boolean;
}

// Tag reference as embedded in project/task responses
export interface TagRef {
  id: string;
  name: string;
}

// Tag vocabulary entry as returned by GET /tags.json and GET /api/v1/tags
export interface TagSummary {
  id: string;
  name: string;
  count: number;
  lastUsedAt: string | null;
}

export interface Task {
  id: string;
  projectId: string;
  title: string;
  notes: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  position: number;
  isActive: boolean;
  tags?: TagRef[];
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Write-only input field for update payloads: array of tag NAME strings
   * (full replacement). Never present in server responses — the response
   * shape uses `tags`. See Taggable concern (app/models/concerns/taggable.rb).
   */
  tag_names?: string[];
}

// Now (daily commitment slots)
export interface NowItem {
  taskId: string;
  projectId: string;
  projectTitle: string;
  title: string;
  priority: TaskPriority;
  status: TaskStatus;
  currentStayDays: number;
  position?: number;
  addedAt?: string;
  completedAt?: string;
  currentStayStartedOn?: string;
}

export interface NowSuggestion {
  taskId: string;
  projectId: string;
  projectTitle: string;
  title: string;
  priority: TaskPriority;
  status: TaskStatus;
}

export type CycleCadence = 'weekly' | 'monthly' | 'quarterly' | 'yearly';
export type CycleStatus = 'unstarted' | 'in_progress' | 'done' | 'skipped' | 'not_applicable';

export type CycleCategoryColor =
  'slate' | 'red' | 'orange' | 'amber' | 'green' | 'teal' | 'blue' | 'indigo' | 'purple' | 'pink';

export interface CycleCategory {
  id: string;
  name: string;
  color: CycleCategoryColor;
}

export interface CycleItemAction {
  id: string;
  title: string;
  position: number;
}

export interface CycleOccurrenceAction {
  id: string;
  title: string;
  position: number;
  completed: boolean;
}

export interface CycleItem {
  id: string;
  title: string;
  category: string;
  cadence: CycleCadence;
  instructions: string | null;
  resourceUrl: string | null;
  estimatedDuration: string | null;
  archivedAt: string | null;
  /** Older cached payloads may not include this newly persisted order yet. */
  position?: number;
  actions: CycleItemAction[];
}

export interface CycleOccurrence {
  id: string;
  itemId: string;
  cadence: CycleCadence;
  periodKey: string;
  title: string;
  category: string;
  instructions: string | null;
  resourceUrl: string | null;
  estimatedDuration: string | null;
  status: CycleStatus;
  notes: string | null;
  updatedAt: string;
  actions: CycleOccurrenceAction[];
}

export interface CycleNudgePreferences {
  enabled: boolean;
  highlight_items: boolean;
  thresholds: Record<CycleCadence, number>;
}

export interface CyclesPayload {
  /** Present on create responses so Undo cannot mistake a concurrent item for the new item. */
  createdItemId?: string;
  currentOccurrences: CycleOccurrence[];
  items: CycleItem[];
  historyOccurrences: CycleOccurrence[];
  currentPeriods: Record<CycleCadence, string>;
  currentPeriodEnds?: Record<CycleCadence, string>;
  categories: CycleCategory[];
  cycleNudgePreferences?: CycleNudgePreferences;
}

export type HabitLogStatus = 'completed' | 'missed';

export interface HabitCategory {
  id: string;
  name: string;
  color: string;
  tagline: string | null;
  completionThresholdPercent: number;
  position: number;
}

export interface Habit {
  id: string;
  title: string;
  description: string | null;
  categoryId: string | null;
  startDate: string;
  archivedAt: string | null;
  position: number;
}

export interface HabitLog {
  habitId: string;
  localDate: string;
  status: HabitLogStatus;
}

export interface HabitsPayload {
  /** Present on create-category responses so the picker can select the new row. */
  createdCategoryId?: string;
  range: { startDate: string; endDate: string; dates: string[] };
  habits: Habit[];
  logs: HabitLog[];
  /**
   * The logs before `range`, back to `startDate`. Only sent when the request
   * asks for it (`?history=1`: phone Today mode, for streaks).
   */
  history?: { startDate: string; logs: HabitLog[] };
  categories: HabitCategory[];
  today: string;
}

export interface NowState {
  capacity: number;
  refillSameDay: boolean;
  localDate: string;
  items: NowItem[];
  completedToday: NowItem[];
  occupied: number;
  canAdd: boolean;
}

export type NowDensity = 'compact' | 'normal' | 'large' | 'hidden';
export type NowVisibleDensity = Exclude<NowDensity, 'hidden'>;

export const NOW_DENSITIES: NowDensity[] = ['compact', 'normal', 'large', 'hidden'];
export const NOW_VISIBLE_DENSITIES: NowVisibleDensity[] = ['compact', 'normal', 'large'];

/** Now board card refresh interval in seconds. 0 disables polling. */
export const NOW_BOARD_POLL_SECONDS = [0, 30, 60, 120, 300] as const;
export type NowBoardPollSeconds = (typeof NOW_BOARD_POLL_SECONDS)[number];
export const DEFAULT_NOW_BOARD_POLL_SECONDS: NowBoardPollSeconds = 60;

export const DEFAULT_NOW_STATE: NowState = {
  capacity: 3,
  refillSameDay: false,
  localDate: '',
  items: [],
  completedToday: [],
  occupied: 0,
  canAdd: true,
};

// Project with full task list (for detail views)
export interface ProjectWithTasks extends Project {
  tasks: Task[];
}

// Sibling project reference (for "move task to" feature)
export interface ProjectRef {
  id: string;
  title: string;
  category: ProjectCategory;
}

export type CalendarEventType = 'appointment' | 'travel' | 'event' | 'deadline';

export interface CalendarEvent {
  id: string;
  title: string;
  date: string; // start date in user's timezone (YYYY-MM-DD)
  endDate?: string | null; // end date for multi-day events, null if single day
  startTime?: string | null; // HH:MM in user's timezone, null for all-day events
  endTime?: string | null; // HH:MM in user's timezone, null for all-day events
  type: CalendarEventType;
  color?: string;
  notes?: string;
}

// Helper to check if an event is all-day (no specific time)
export function isAllDayEvent(event: CalendarEvent): boolean {
  return !event.startTime;
}

// Projects grouped by status for a single month/week
export type ProjectsByStatus = Record<ProjectStatus, Project[]>;

// === New View System ===

// Scale determines the time granularity
export type Scale = 'month' | 'week' | 'year';

// View types (scale-agnostic)
// Primary views (shown as buttons)
export type PrimaryView = 'days' | 'rolling' | 'focus';
// Secondary views (in "More" dropdown)
export type SecondaryView = 'table' | 'timeline' | 'gantt' | 'grid-6x2' | 'grid-quarters' | 'tasks';
// All views
export type View = PrimaryView | SecondaryView;

// Range options by scale (how much time is shown)
export type MonthRange = '1-month' | 'quarter' | '3-months' | '6-months' | 'year';
export type WeekRange = '1-week' | 'month' | '5' | '7' | 'quarter';
export type YearRange = 'full' | 'half' | 'quarter';
export type Range = MonthRange | WeekRange | YearRange | null;

// Display filter: what content to show
export type DisplayFilter = 'both' | 'projects' | 'events';

// View configuration passed from backend (derived from VIEW_REGISTRY)
export interface ViewConfig {
  primaryViews: PrimaryView[];
  secondaryViews: SecondaryView[];
  viewsByScale: Record<Scale, View[]>;
  defaultViewForScale: Record<Scale, View>;
  validRanges: Record<Scale, string[]>;
  defaultRanges: Record<Scale, string | null>;
  viewsWithRangeSupport: View[];
  viewSpecificRanges: Partial<Record<View, Partial<Record<Scale, string[]>>>>;
  viewSpecificDefaultRanges: Partial<Record<View, Partial<Record<Scale, string>>>>;
  validDisplayOptions: Partial<Record<View, DisplayFilter[]>>;
  defaultDisplay: Partial<Record<View, DisplayFilter>>;
  // New fields from VIEW_REGISTRY
  viewLabels: Record<View, { label: string; icon: string }>;
  fullWidthViews: View[];
}

// Props passed from DashboardController
export interface DashboardProps {
  backlogProjects: Project[];
  scheduledProjects: Project[];
  projectsByMonth: Record<string, ProjectsByStatus>;
  projectsByWeek: Record<string, ProjectsByStatus>;
  events: CalendarEvent[];
  currentMonth: string;
  currentWeek: string;
  currentYear: number;
  visibleMonths: string[];
  visibleWeeks: string[];
  scale: Scale;
  view: View;
  range: Range;
  display: DisplayFilter | null;
  viewConfig: ViewConfig;
}

// Period types used by individual view components for their range selectors
export type WeekPeriod = '1-week' | 'month' | '5' | '7' | 'quarter';
export type TablePeriod = 'full' | 'half' | 'quarter';

// === Helper Functions ===

// Check if a view is valid for a given scale
export function isViewValidForScale(view: View, scale: Scale, viewConfig: ViewConfig): boolean {
  return viewConfig.viewsByScale[scale]?.includes(view) ?? false;
}

// Get the default view for a scale
export function getDefaultViewForScale(scale: Scale, viewConfig: ViewConfig): View {
  return viewConfig.defaultViewForScale[scale] ?? 'focus';
}

// Get valid ranges for a scale
export function getValidRangesForScale(scale: Scale, viewConfig: ViewConfig): string[] {
  return viewConfig.validRanges[scale] ?? [];
}

// Get valid ranges for a view+scale (considers view-specific overrides)
export function getValidRangesForView(view: View, scale: Scale, viewConfig: ViewConfig): string[] {
  const viewSpecific = viewConfig.viewSpecificRanges?.[view]?.[scale];
  if (viewSpecific && viewSpecific.length > 0) {
    return viewSpecific;
  }
  return viewConfig.validRanges[scale] ?? [];
}

// Get default range for a view+scale (considers view-specific overrides)
export function getDefaultRangeForView(
  view: View,
  scale: Scale,
  viewConfig: ViewConfig,
): string | null {
  const viewSpecific = viewConfig.viewSpecificDefaultRanges?.[view]?.[scale];
  if (viewSpecific) {
    return viewSpecific;
  }
  return viewConfig.defaultRanges[scale] ?? null;
}

// Check if scale has ranges
export function scaleHasRanges(scale: Scale, viewConfig: ViewConfig): boolean {
  return getValidRangesForScale(scale, viewConfig).length > 0;
}

// Get valid display options for a view
export function getValidDisplayOptions(view: View, viewConfig: ViewConfig): DisplayFilter[] {
  return (viewConfig.validDisplayOptions[view] as DisplayFilter[]) ?? [];
}

// Get default display for a view
export function getDefaultDisplay(view: View, viewConfig: ViewConfig): DisplayFilter | null {
  return viewConfig.defaultDisplay[view] ?? null;
}

// Check if a view supports the display filter
export function viewHasDisplayFilter(view: View, viewConfig: ViewConfig): boolean {
  return getValidDisplayOptions(view, viewConfig).length > 0;
}

// === Favorite Views ===

export type FavoritePageType = 'timeline' | 'calendar';
export type CalendarLayout = 'big-year' | 'sequential' | 'weekday' | 'wall' | 'time-blocks';

export const CALENDAR_LAYOUT_LABELS: Record<CalendarLayout, string> = {
  'big-year': 'Big Year',
  sequential: 'Sequential',
  weekday: 'Weekday',
  wall: 'Wall',
  'time-blocks': 'Time Blocks',
};

export interface FavoriteView {
  id: string;
  name: string;
  icon?: string;
  page_type?: FavoritePageType; // defaults to 'timeline' if absent
  // Timeline favorites (required when page_type is 'timeline' or absent)
  view?: View;
  scale?: Scale;
  range?: Range;
  display?: DisplayFilter | null;
  // Calendar favorites (required when page_type is 'calendar')
  layout?: CalendarLayout;
  position: number;
}

// Helper to get effective page type (backward compat: absent = 'timeline')
export function getFavoritePageType(fav: FavoriteView): FavoritePageType {
  return fav.page_type || 'timeline';
}

export interface NavbarConfig {
  show_default_views: boolean;
  hidden_default_views: string[];
  navbar_views: string[];
}

export const DEFAULT_NAVBAR_VIEWS = ['days', 'rolling', 'focus'];
export const MAX_NAVBAR_VIEWS = 3;
export const MIN_NAVBAR_VIEWS = 1;

// Convenience: should projects be shown?
export function showProjects(display: DisplayFilter | null): boolean {
  return display === null || display === 'both' || display === 'projects';
}

// Convenience: should events be shown?
export function showEvents(display: DisplayFilter | null): boolean {
  return display === null || display === 'both' || display === 'events';
}

// ==========================================
// BOARDS (freeform canvas views)
// ==========================================

export type BoardItemKind = 'project_card' | 'text' | 'shape';
export type ShapeVariant = 'rectangle' | 'ellipse' | 'diamond';

export interface BoardViewport {
  x: number;
  y: number;
  zoom: number;
}

/** Presentational options stored per item. All keys optional and free-form-ish. */
export interface BoardItemStyle {
  fill?: string;
  stroke?: string;
  textColor?: string;
  fontSize?: string;
  fontWeight?: string;
  fontStyle?: string;
  textAlign?: string;
  verticalAlign?: string;
  shapeVariant?: ShapeVariant;
  opacity?: string;
}

export interface BoardItem {
  id: string;
  kind: BoardItemKind;
  projectId: string | null;
  x: number;
  y: number;
  width: number | null;
  height: number | null;
  zIndex: number;
  style: BoardItemStyle;
  content: string | null;
}

export interface BoardSummary {
  id: string;
  name: string;
  viewport: BoardViewport;
  itemCount: number;
  updatedAt: string;
}
